// Shared loading / error / disabled-entitlement banner for manufacturing
// mode panels. Each pilot + scaffold panel calls
// `<ManufacturingGateBanner gate={gate} />` right under its `<h2>`; when
// the gate is healthy and entitled the banner returns null so the
// panel's SectionCards can take over.
import type { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";

type Props = {
  gate: ReturnType<typeof useManufacturingEntitlementGate>;
};

export function ManufacturingGateBanner({ gate }: Props) {
  if (gate.loading) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
        Loading manufacturing data…
      </div>
    );
  }
  if (gate.error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
        {gate.error}
      </div>
    );
  }
  if (!gate.enabled) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
        Manufacturing Suite add-on is not enabled for this organization.
      </div>
    );
  }
  return null;
}

/**
 * Tailwind toast bubble used by panels that surface short-lived feedback
 * after an async action (e.g. "Material created", "3 line(s) received").
 * Caller owns the auto-dismiss timer.
 */
export function ManufacturingToast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="rounded-xl bg-gray-900/90 px-3 py-2 text-xs text-white shadow-md dark:bg-zinc-100/90 dark:text-zinc-900">
      {message}
    </div>
  );
}
